import {
  Component,
  AfterViewInit,
  ViewChild,
  ElementRef,
  OnDestroy,
  NgZone,
  inject,
} from '@angular/core';
import { BannerMode, chooseBannerMode } from './banner-performance.service';

@Component({
  selector: 'app-banner',
  standalone: true,
  imports: [],
  templateUrl: './banner.component.html',
  styleUrl: './banner.component.scss',
  host: {
    '[class.animations-enabled]': "mode === 'full'",
  },
})
export class BannerComponent implements AfterViewInit, OnDestroy {
  @ViewChild('waveCanvas') canvasRef!: ElementRef<HTMLCanvasElement>;

  private ctx: CanvasRenderingContext2D | null = null;
  private width = 0;
  private height = 0;

  private increment = 0;
  private animationId = 0;
  private lastTimestamp = 0;

  private isMobile = false;

  private mode: BannerMode = 'full';

  private ngZone = inject(NgZone);

  private waves = [
    {
      yOffset: 90,
      color: 'rgba(0, 255, 242, 0.6)',
      strokeColor: 'rgba(0, 255, 242, 0.6)',
      speed: 0.007,
      amplitude: 35,
      frequency: 0.004,
    },
    {
      yOffset: 120,
      color: 'rgba(30, 144, 255, 0.5)',
      strokeColor: 'rgba(30, 144, 255, 0.5)',
      speed: 0.006,
      amplitude: 45,
      frequency: 0.0035,
    },
    {
      yOffset: 150,
      color: 'rgba(64, 224, 208, 0.4)',
      strokeColor: 'rgba(64, 224, 208, 0.4)',
      speed: 0.005,
      amplitude: 40,
      frequency: 0.003,
    },
    {
      yOffset: 180,
      color: 'rgba(65, 105, 225, 0.3)',
      strokeColor: 'rgba(65, 105, 225, 0.3)',
      speed: 0.004,
      amplitude: 55,
      frequency: 0.0025,
    },
  ];

  async ngAfterViewInit() {
    let canvas = this.canvasRef?.nativeElement;
    if (!canvas) {
      canvas = document.getElementById('waveCanvas') as HTMLCanvasElement;
    }

    if (!canvas) {
      return;
    }

    this.ctx = canvas.getContext('2d');
    if (!this.ctx) return;

    this.resize(canvas);
    window.addEventListener('resize', () => this.resize(canvas));
    this.ngZone.runOutsideAngular(() => {
      this.animationId = requestAnimationFrame((timestamp) => this.animate(canvas, timestamp));
    });

    const mode = await chooseBannerMode();
    this.applyMode(canvas, mode);
  }

  ngOnDestroy() {
    cancelAnimationFrame(this.animationId);
  }

  private applyMode(canvas: HTMLCanvasElement, mode: BannerMode) {
    this.mode = mode;

    if (mode === 'disabled') {
      cancelAnimationFrame(this.animationId);
      canvas.style.display = 'none';
    }
  }

  private resize(canvas: HTMLCanvasElement) {
    const parent = canvas.parentElement;
    const domWidth = window.innerWidth;

    if (parent) {
      this.width = canvas.width = parent.offsetWidth;
      this.height = canvas.height = parent.offsetHeight;
      this.isMobile = this.width <= 550 && domWidth <= 550;
    }
  }

  private animate(canvas: HTMLCanvasElement, timestamp: number) {
    if (!this.ctx || !canvas || this.mode === 'disabled') return;

    const deltaMs = this.lastTimestamp ? timestamp - this.lastTimestamp : 1000 / 60;
    this.lastTimestamp = timestamp;

    this.draw();
    this.increment += 0.03 * (deltaMs / (1000 / 60));
    this.animationId = requestAnimationFrame((ts) => this.animate(canvas, ts));
  }

  private draw() {
    if (!this.ctx) return;

    this.ctx.clearRect(0, 0, this.width, this.height);

    this.waves.forEach((wave) => {
      const adjustedOffset = this.isMobile ? wave.yOffset * 0.2 : wave.yOffset;
      const adjustedAmplitude = this.isMobile ? wave.amplitude * 0.5 : wave.amplitude;

      this.ctx!.beginPath();
      for (let i = 0; i <= this.width; i += 1) {
        const y =
          Math.sin(i * wave.frequency + this.increment * wave.speed * 80) * adjustedAmplitude;
        if (i === 0) {
          this.ctx!.moveTo(i, this.height - adjustedOffset + y);
        } else {
          this.ctx!.lineTo(i, this.height - adjustedOffset + y);
        }
      }

      this.ctx!.lineWidth = 2.5;
      this.ctx!.strokeStyle = wave.strokeColor;
      this.ctx!.shadowBlur = 15;
      this.ctx!.shadowColor = wave.color;

      this.ctx!.stroke();
      this.ctx!.shadowBlur = 0;
    });
  }
}
